import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { Autocomplete, Button, Input, Modal, Spinner } from "src/components";
import { Fragment, useEffect, useRef, useState } from "react";  //M10-12866
import SuccessIcon from "../../assets/successCircle.svg";
import EditIcon from "../../../../assets/edit-pen.svg"; //M10-14023
import CancelCircleIcon from "../../assets/cancelCircle.svg";
import { getActionPermission, getCounterByEmployee, getCounterOrderProductsById, getOrderDetailsByOrderCode2, getPoOrderDetailsByStockistPo, getUinDetails, orderApproveRejectActionPermission, PoPlaceOrder, putAcceptOrderRequest } from "src/services/order-api"; //M10-12866  //M10-14023
import { useEventStatus } from "src/hooks/useEventStatus";
import { useToast } from "src/hooks/useToast";
import useOrderUinConfirmation from "src/hooks/useOrderUinConfirmation";
import FormSuccess from "../../container/FormSuccess";
import OrderApprovalForm from "../OrderDetailsTab/OrderApprovalForm";
import OrderInfoInvoiceProducts from "./OrderInfoTabCards/OrderInfoInvoiceProducts";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { useLocation, useParams } from "react-router-dom";  //M10-13811
import Loader from "../../components/Loader";
import { ordActions } from "src/context/order-slice";
import { useDispatch } from "react-redux";
import useDebounceLeading from "src/hooks/useDebounceLeading";  //M10-12866
import SmartForm from "src/container/smart-form.component";
import * as yup from "yup";
import GridProductAutocomplete from "./GridProductAutocomplete";
import { generateUniqueID } from "src/constants/functions";
import { downloadInvoice, getInvoicePriceList } from "src/services/invoice-api";
import EyeShowIcon from "src/assets/eye-show.svg";
import usePermission from "src/hooks/usePermission"; //M10-16393
import OrderQtyEditModal from "./OrderQtyEditModal"; //M10-16393
//M10-12866
interface PoQuantityItem {
  order_product_id: string;       // Product ID
  stockist_po_quantity: number; // Quantity entered by stockist
  stockist_po_freequantity: number
}

interface NewPoProductItem {
  product_name: string;
  item_code: string;
  product_id: string | number | null;
  item_division_id: number | null;
  ucode: string;
  company_division_code: number | null;
  quantity: string;
  stockist_po_quantity: string;
  stockist_po_freequantity: string;
}
interface PoFormData {
  poid_ref_number: string;
  poid_date: string;           // Format: YYYY-MM-DD
  po_valid_date: string;       // Format: YYYY-MM-DD
  stockist_po_number: string;
  orderProducts: PoQuantityItem[]; // Array of quantity objects
  newOrderProducts: NewPoProductItem[]; // Manually added products
}
//M10-12866

const OrderInfoDetailsTab = () => {
  const { orderDetailsFiltered, OrderFilters } = useSelector((state: RootState) => state.ord);
  const [actionList, setActionList] = useState<string[]>([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);
  const [actionToPerformAfterUinVerification, setActionToPerformAfterUinVerification] = useState<string>("");
  const { currentPsr } = useSelector((state: RootState) => state.auth);
  const [uinDetails, setUinDetails] = useState<any>(null);
  const [isFetchingOrderData, setIsFetchingOrderData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [actionType, setActionType] = useState("")
  //M10-14023
  const editProductsSchema = yup.object().shape({
    remarks: yup
      .string()
      .trim()
      .required("Remarks are required")
      .max(500, "Remarks must not exceed 500 characters"),
  });

  type EditProductRow = {
    order_product_id: number;
    product_name: string;
    quantity: number;
    min_qty: number;
    scheme_value: number;
    price_identifier: number;
    new_qty: string;
    item_code: string;
  };

  const [editProductsModal, setEditProductsModal] = useState<{
    isOpen: boolean;
    products: EditProductRow[];
  }>({ isOpen: false, products: [] });
  const [isSubmittingEditProducts, setIsSubmittingEditProducts] = useState(false);

  //M10-16393 — order:edit-quantity right: increase / decrease quantities (ORDER_EDIT order_qty_edit)
  const { hasPermission: hasEditQuantityPermission } = usePermission({
    module: fineGrainedPermissionsKeys.ORDER.EDITQUANTITY.module,
    action: fineGrainedPermissionsKeys.ORDER.EDITQUANTITY.action,
  });
  const [isQtyEditOpen, setIsQtyEditOpen] = useState(false);

  const openEditProductsModal = () => {
    const products: EditProductRow[] = (orderDetails?.orderProducts || []).map((p: any) => ({
      order_product_id: p?.order_product_id,
      product_name: p?.product_name ?? "",
      quantity: Number(p?.quantity ?? 0),
      new_qty: String(p?.quantity ?? ""),
      min_qty: Number(p?.min_qty),
      scheme_value: Number(p?.free_quantity ?? 0),
      price_identifier: Number(p?.price_identifier ?? 0),
      item_code: p?.item_code ?? 0,
    }));
    setEditProductsModal({ isOpen: true, products });
  };

  const closeEditProductsModal = () => {
    setEditProductsModal({ isOpen: false, products: [] });
  };

  const handleEditQtyChange = (idx: number, value: string) => {
    setEditProductsModal((prev) => ({
      ...prev,
      products: prev.products.map((p, i) => (i === idx ? { ...p, new_qty: value } : p)),
    }));
  };

  const getDerivedFreeQty = (p: EditProductRow): number => {
    const newQty = Number(p.new_qty);
    if (!Number.isFinite(newQty) || newQty <= 0) return 0;
    if (p.quantity <= 0 || p.scheme_value <= 0) return 0;
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(p.quantity, p.scheme_value);
    const reducedQty = p.quantity / divisor;
    const reducedScheme = p.scheme_value / divisor;
    return Math.floor(newQty / reducedQty) * reducedScheme;
  };

  const getQtyError = (p: EditProductRow): string => {
    const raw = (p.new_qty ?? "").toString().trim();
    if (raw === "") return "Quantity is required";
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return "Must be a whole number";
    if (n < 0) return "Must be non-negative";
    if (n > p.quantity) return `Cannot exceed current qty (${p.quantity})`;
    const minQty = Number.isFinite(p.min_qty) && p.min_qty > 0 ? p.min_qty : 0;
    if (n > 0 && minQty > 0 && n < minQty) return `Must be 0 or at least ${minQty}`;
    return "";
  };

  const handleSubmitEditProducts = async (data: any) => {
    if (isSubmittingEditProducts) return;

    const remarks = (data?.remarks ?? "").trim();
    const products = editProductsModal.products;

    if (products.length === 0) {
      toast({ description: "No products to update", variant: "destructive" });
      return;
    }

    for (const p of products) {
      const raw = (p.new_qty ?? "").toString().trim();
      if (raw === "") {
        toast({ description: `Enter new qty for ${p.product_name}`, variant: "destructive" });
        return;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
        toast({
          description: `New qty for ${p.product_name} must be a non-negative integer`,
          variant: "destructive",
        });
        return;
      }
      if (n > p.quantity) {
        toast({
          description: `New qty for ${p.product_name} cannot exceed current qty (${p.quantity})`,
          variant: "destructive",
        });
        return;
      }
      const minQty = Number.isFinite(p.min_qty) && p.min_qty > 0 ? p.min_qty : 0;
      if (n > 0 && minQty > 0 && n < minQty) {
        toast({
          description: `New qty for ${p.product_name} must be 0 or at least ${minQty}`,
          variant: "destructive",
        });
        return;
      }
    }

    const payload = {
      data: {
        order_id: orderDetails?.order_id_int,
        position_code: currentPsr?.emp_position_hdr?.position_code,
        action: "approve",
        remarks,
        product: products.map((p) => ({
          order_product_id: p.order_product_id,
          new_qty: Number(p.new_qty),
        })),
      },
      event_type: "ORDER_APPROVE",
      primary_key: orderDetails?.order_id_int,
    };

    setIsSubmittingEditProducts(true);
    try {
      const res = await putAcceptOrderRequest(payload, orderDetails?.company_code);
      if (res?.status !== 200) throw new Error(res?.data?.message);
      toast({
        description: res?.data?.message || "Order approved with updated quantities",
        variant: "default",
      });
      const streamId = res?.data?.data?.data?.eventStreamCreated?.streamId;
      closeEditProductsModal();
      fetchOrderId(orderCode as string, streamId);
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message || "Failed to submit edited quantities",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingEditProducts(false);
    }
  };
  //M10-14023

  const [modalStates, setModalStates] = useState({
    approveCounter: false,
    rejectCounter: false,
    errorForm: false,
    userConfirm: false,
    order_id: 0,
  });

  const orderDetails = orderDetailsFiltered?.[0];
  //M10-12866
  const formatDateForInput = (dateString: any) => {
    if (!dateString) return "";
    return new Date(dateString).toISOString().split("T")[0];
  };
  const [isPoEditMode, setIsPoEditMode] = useState(false);
  const [poFormData, setPoFormData] = useState<PoFormData>({
    poid_ref_number: orderDetails?.poid_ref_number,
    poid_date: formatDateForInput(orderDetails?.poid_date),
    po_valid_date: formatDateForInput(orderDetails?.po_valid_date),
    stockist_po_number: orderDetails?.stockist_po_number,
    orderProducts: [],
    newOrderProducts: [],
  });
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const productOptionsLoaded = useRef(false);
  const [poViewData, setPoViewData] = useState<any[] | null>(null);
  const initialPoViewDataRef = useRef<any[] | null>(null);
  const [isLoadingPoView, setIsLoadingPoView] = useState(false);
  const poUpdateTimers = useRef<Record<number, any>>({});

  // Invoice price list modal state
  const [priceListModalOpen, setPriceListModalOpen] = useState(false);
  const [priceListData, setPriceListData] = useState<any[]>([]);
  const [isLoadingPriceList, setIsLoadingPriceList] = useState(false);
  const [priceListItemCode, setPriceListItemCode] = useState<string>("");

  const handleOpenPriceList = async (itemCode: string) => {
    if (!itemCode) {
      toast({
        description: "Please select a product first",
        variant: "destructive",
      });
      return;
    }
    setPriceListItemCode(itemCode);
    setPriceListModalOpen(true);
    setIsLoadingPriceList(true);
    setPriceListData([]);
    try {
      const res: any = await getInvoicePriceList(itemCode);
      setPriceListData(res?.data?.data || []);
    } catch (err) {
      console.log(err);
      setPriceListData([]);
    } finally {
      setIsLoadingPriceList(false);
    }
  };

  const isRequestInProgress = useRef(false);
  const handleApproval = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    // Validation
    if (!poFormData.poid_ref_number || !poFormData.poid_date || !poFormData.po_valid_date) {
      toast({
        description: "Please fill all required fields",
        variant: "destructive"
      });
      isRequestInProgress.current = false;
      return;
    }

    const invalidNewProduct = poFormData.newOrderProducts.find(
      (row) =>
        !row.product_name ||
        !row.item_code ||
        !row.stockist_po_quantity
    );
    if (invalidNewProduct) {
      toast({
        description: "Please fill product, code, order qty and PO qty for all added products",
        variant: "destructive",
      });
      isRequestInProgress.current = false;
      return;
    }

    const duplicateNewProduct = poFormData.newOrderProducts.find((row, i) =>
      isDuplicateNewProduct(i, row.item_code)
    );
    if (duplicateNewProduct) {
      toast({
        description: "Duplicate product detected. Please remove duplicates before submitting.",
        variant: "destructive",
      });
      isRequestInProgress.current = false;
      return;
    }

    const newProductsMapped = poFormData.newOrderProducts.map((row, idx) => ({
      order_product_id: 0,
      item_code: row.item_code,
      product_name: row.product_name,
      ucode: row.ucode || generateUniqueID(),
      item_division_id: row.item_division_id ?? 0,
      sequence_number: idx + 1,
      company_division_code: row.company_division_code ?? orderDetails?.company_divisioncode ?? 0,
      module_code: 4,
      price_identifier: 1,
      scheme_value: 0,
      quantity: Number(row.quantity) || 0,
      stockist_po_quantity: Number(row.stockist_po_quantity) || 0,
      stockist_po_freequantity: Number(row.stockist_po_freequantity) || 0,
    }));

    const num = (v: any) => Number(v ?? 0) || 0;

    const changedOrderProducts = (poFormData.orderProducts || []).filter((p) => {
      const original = orderDetails?.orderProducts?.find(
        (o: any) => o?.order_product_id === p?.order_product_id
      );
      if (!original) return true;
      return (
        num(p?.stockist_po_quantity) !== num(original?.stockist_po_quantity) ||
        num(p?.stockist_po_freequantity) !== num(original?.stockist_po_freequantity)
      );
    });

    const changedPoView = (poViewData || []).filter((row: any) => {
      const initial = initialPoViewDataRef.current?.find(
        (o: any) => o?.order_product_id === row?.order_product_id
      );
      if (!initial) return true;
      return (
        num(row?.stockist_po_quantity) !== num(initial?.stockist_po_quantity) ||
        num(row?.stockist_po_freequantity) !== num(initial?.stockist_po_freequantity) ||
        row?.product_name !== initial?.product_name ||
        row?.item_code !== initial?.item_code ||
        row?.product_id !== initial?.product_id
      );
    });

    const poViewMapped = changedPoView.map((row: any) => ({
      order_product_id: row?._isNew ? 0 : (row?.order_product_id ?? 0),
      stockist_po_quantity: num(row?.stockist_po_quantity),
      stockist_po_freequantity: num(row?.stockist_po_freequantity),
    }));

    const orderPayload = {
      data: {
        order_id_int: orderDetails?.order_id_int,
        order_code: orderDetails?.order_code,
        reference_number: poFormData.stockist_po_number,
        poid_ref_number: poFormData.poid_ref_number,
        poid_date: poFormData.poid_date,
        po_valid_date: poFormData.po_valid_date,
        stockist_po_number: poFormData.stockist_po_number,
        stockist_po_attachment_id: location?.state?.stockist_po_attachment_id,
        edit_type:"kam_edit",
        module_code: 4,
        // only changed existing rows + new rows + changed PO view rows
        orderProducts: [
          ...changedOrderProducts,
          ...newProductsMapped,
          ...poViewMapped,
        ],
        // newOrderProducts intentionally omitted
      },
      event_type: "ORDER_EDIT",
      primary_key: orderDetails?.order_id_int,
    };

    try {
      const res = await PoPlaceOrder(orderPayload, orderDetails?.company_code);
      if (res?.status !== 200) throw new Error(res?.data?.message);
      toast({
        description: "PO details updated successfully",
      });
      // Close the form
      setIsPoEditMode(false);
      fetchOrderId(orderCode as string);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        "Something went wrong";
      toast({
        variant: "destructive",
        description: errorMessage,
      });
    } finally {
      isRequestInProgress.current = false;
    }
  }, 1000);
  //M10-12866
  const { toast } = useToast();
  const { withEventCheck } = useEventStatus({
    retries: 10,
    retryDelay: 1000,
  });
  const { orderCode } = useParams();
  //M10-13811
  const location = useLocation();
  const autoOpenPoEdit = (location?.state as { autoOpenPoEdit?: boolean } | null)?.autoOpenPoEdit === true;
  // Only the status-2 deep-link should auto-open the Product-wise PO Quantities (View) section.
  const autoOpenPoView = (location?.state as { autoOpenPoView?: boolean } | null)?.autoOpenPoView === true;
  const poFilePathFromState = (location?.state as { po_file_path?: string } | null)?.po_file_path;
  const hasAutoOpenedPoEdit = useRef(false);
  const hasAutoOpenedPoView = useRef(false);
  //M10-13811
  const dispatch = useDispatch();

  const totalQuantity = orderDetailsFiltered?.[0]?.orderProducts?.reduce((acc: number, curr: any) => acc + curr?.quantity, 0);
  const totalBilledPrice = orderDetailsFiltered?.[0]?.orderProducts?.reduce((acc: number, curr: any) => acc + Number(curr?.billed_price), 0);

  const {
    modal,
    isOpen: checkingUINOpen,
    checkUINValidation,
    isLoading: checkingUINLoading,
  } = useOrderUinConfirmation({
    orderCode: orderDetails?.order_code,
    isApproval: true,
    divisionCode: uinDetails?.uin_exemption
      ? []
      : uinDetails?.require_uin_status && uinDetails?.is_uin_required
        ? [
          {
            counterDivisionCode: uinDetails?.company_division_code?.company_divisioncode,
            expired:
              uinDetails?.uin_last_verified_date &&
              (new Date().getTime() - new Date(uinDetails?.uin_last_verified_date).getTime()) / (1000 * 60 * 60 * 24) > 30,
          },
        ]
        : [],
    counterCode: orderDetails?.counter_code,
    counterLocationCode: orderDetails?.counterInfo?.location_code,
    divisionProductsData: [orderDetails],
    onUinVerified: () => {
      checkPermission(
        actionToPerformAfterUinVerification && actionToPerformAfterUinVerification,
        currentPsr?.emp_position_hdr?.position_code,
        orderDetails?.order_id_int,
        () => {
          if (actionToPerformAfterUinVerification === "approve") {
            setModalStates({ ...modalStates, approveCounter: true });
          } else {
            setModalStates({ ...modalStates, rejectCounter: true });
          }
        }
      );
    },
  });

  const resetForm = () => {
    setModalStates({
      approveCounter: false,
      rejectCounter: false,
      errorForm: false,
      userConfirm: false,
      order_id: 0,
    });
  };

  const closeModal = (modalName: string) => {
    setModalStates((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
    setActionToPerformAfterUinVerification("");
  };

  const checkPermission = async (action: string, position_code: string, order_id: number, callback: any) => {
    if (isCheckingPermission) return;
    setIsCheckingPermission(true);
    try {
      const response = await orderApproveRejectActionPermission(position_code, action, order_id, orderDetails?.company_code);
      setIsCheckingPermission(false);
      // if (response.status !== 200) {
      //   throw new Error();
      // }
      if (response?.data?.data?.allowed === true) {
        callback();
      } else {
        setModalStates((prev) => {
          return { ...prev, errorForm: true };
        });
      }
    } catch (error: any) {
      console.log(error);
      setIsCheckingPermission(false);
      toast({ description: "Something went wrong", variant: "destructive" });
      setModalStates((prev) => {
        return { ...prev, errorForm: true };
      });
    }
  };

  const fetchActionPermission = async (streamId?: string) => {
    if (isCheckingPermission) return;
    setIsCheckingPermission(true);
    //M10-12758
    const company = window.sessionStorage.getItem("companyFilter");
    const company_code = window.localStorage.getItem("company_code");
    //M10-12758

    setActionList([]);
    if (currentPsr?.emp_position_hdr?.position_code) {
      try {
        const response = await withEventCheck(
          () =>
            getActionPermission(
              currentPsr?.emp_position_hdr?.position_code,
              orderDetails?.order_id_int,
              orderDetails?.company_code
            ),
          streamId,
          Number(orderDetails?.company_code) || Number(company) || Number(company_code) //M10-12758
        );
        setIsCheckingPermission(false);
        setActionList(response?.data?.data?.operation_list);
      } catch (error: any) {
        console.log(error);
        setIsCheckingPermission(false);
        resetForm();
      } finally {
        setIsCheckingPermission(false);
      }
    }
  };

  const fetchOrderId = async (orderCode: string, streamId?: string) => {
    setIsFetchingOrderData(true);
    try {
      const company = window.sessionStorage.getItem("companyFilter");
      const company_code = window.localStorage.getItem("company_code");
      const stockistPoAttachmentId = (location?.state as { stockist_po_attachment_id?: number | string } | null)?.stockist_po_attachment_id;
      const res = await withEventCheck(
        () => getOrderDetailsByOrderCode2(
          orderCode.toUpperCase(),
          OrderFilters?.company?.[0]?.code || company || company_code,
          stockistPoAttachmentId,
        ),
        streamId,
        OrderFilters?.company?.[0]?.code || Number(company) || Number(company_code) //M10-12758
      );
      dispatch(ordActions.setOrderDetailsFiltered([res?.data?.data]));
      toast({ description: "Order details fetched successfully", variant: "default" });
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setIsFetchingOrderData(false);
    }
  };

  function extractLevel(status: string): string | null {
    // Regex: capture the word(s) between "Waiting for " and " Approval"
    const match = /^Waiting for (.+?) Approval$/i.exec(status?.trim() || "");
    return match ? match[1] : null;
  }

  useEffect(() => {
    if (!orderCode) return;
    const loaded = orderDetailsFiltered?.[0]?.order_code;
    // Skip only if Redux already holds the same order (e.g. parent pre-fetched).
    // Otherwise clear stale data from a previous order and re-fetch.
    if (loaded && String(loaded).toUpperCase() === orderCode.toUpperCase()) return;
    dispatch(ordActions.setOrderDetailsFiltered([]));
    fetchOrderId(orderCode as string);
  }, [orderCode]);

  useEffect(() => {
    if (actionToPerformAfterUinVerification) {
      checkUINValidation();
    }
  }, [actionToPerformAfterUinVerification]);

  useEffect(() => {
    if (orderDetailsFiltered && orderDetailsFiltered.length > 0) {
      fetchActionPermission();
    }
  }, [orderDetailsFiltered]);
  //M10-12866
  const updateDetails = () => {
    setPoFormData({
      poid_ref_number: orderDetails?.poid_ref_number,
      poid_date: formatDateForInput(orderDetails?.poid_date),
      po_valid_date: formatDateForInput(orderDetails?.po_valid_date),
      stockist_po_number: orderDetails?.stockist_po_number,
      orderProducts: orderDetails?.orderProducts?.map((p: any) => ({
        order_product_id: p?.order_product_id,
        stockist_po_quantity: p?.stockist_po_quantity || 0,
        stockist_po_freequantity: p?.stockist_po_freequantity || 0,
      })),
      newOrderProducts: [],
    })
  }

  const fetchProductOptions = async () => {
    if (productOptionsLoaded.current) return;
    if (!orderDetails?.counter_company_location_id) return;
    setIsLoadingProducts(true);
    try {
      const res = await getCounterOrderProductsById(
        String(orderDetails?.counter_company_location_id),
        "",
        "",
        String(orderDetails?.module_code ?? "")
      );
      const list = Array.isArray(res?.data?.data) ? res?.data?.data : [];
      setProductOptions(list);
      productOptionsLoaded.current = true;
    } catch (error) {
      console.error("Error fetching product options:", error);
      toast({ description: "Failed to load product list", variant: "destructive" });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleAddProductRow = () => {
    fetchProductOptions();
    setPoFormData(prev => ({
      ...prev,
      newOrderProducts: [
        ...prev.newOrderProducts,
        {
          product_name: "",
          item_code: "",
          product_id: null,
          item_division_id: null,
          ucode: "",
          company_division_code: null,
          quantity: "",
          stockist_po_quantity: "",
          stockist_po_freequantity: "",
        },
      ],
    }));
  };


  const handleRemoveProductRow = (index: number) => {
    setPoFormData(prev => ({
      ...prev,
      newOrderProducts: prev.newOrderProducts.filter((_, i) => i !== index),
    }));
  };

  const isDuplicateNewProduct = (currentIndex: number, itemCode: string) => {
    if (!itemCode) return false;
    const code = itemCode.trim().toLowerCase();
    const dupInNew = poFormData.newOrderProducts.some(
      (row, i) =>
        i !== currentIndex && (row.item_code || "").trim().toLowerCase() === code
    );
    if (dupInNew) return true;
    return (orderDetails?.orderProducts || []).some(
      (op: any) => (op?.item_code || "").trim().toLowerCase() === code
    );
  };

  const handleNewProductSelect = (index: number, selected: any) => {
    const newItemCode = selected?.companyProducts?.item_code ?? "";
    if (newItemCode && isDuplicateNewProduct(index, newItemCode)) {
      toast({
        description: "This product is already added",
        variant: "destructive",
      });
    }
    setPoFormData(prev => ({
      ...prev,
      newOrderProducts: prev.newOrderProducts.map((row, i) =>
        i === index
          ? {
            ...row,
            product_name: selected?.companyProducts?.name ?? "",
            item_code: selected?.companyProducts?.item_code ?? "",
            product_id: selected?.id ?? null,
            item_division_id: selected?.companyProducts?.item_division?.find((item: any) => item.active_status === "Y")?.item_divisiondtl_id ?? null,
            ucode: selected?.companyProducts?.ucode ?? selected?.ucode ?? "",
            company_division_code:
              selected?.company_division_code ??
              selected?.companyProducts?.company_division_code ??
              null,
            ptr: selected?.companyProducts?.ptr ?? 0
          }
          : row
      ),
    }));
  };

  const handleNewProductFieldChange = (
    index: number,
    field: keyof NewPoProductItem,
    value: string
  ) => {
    setPoFormData(prev => ({
      ...prev,
      newOrderProducts: prev.newOrderProducts.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleViewPoDetails = async () => {
    const stockistPo = (poFormData?.stockist_po_number || "").trim();
    if (!stockistPo) {
      toast({ description: "Enter Stockist PO Number first", variant: "destructive" });
      return;
    }
    if (!orderDetails?.counter_company_location_id) {
      toast({ description: "Counter location not available", variant: "destructive" });
      return;
    }
    setIsLoadingPoView(true);
    try {
      const res = await getPoOrderDetailsByStockistPo(
        orderDetails.counter_company_location_id,
        stockistPo,
        orderDetails.order_id_int,
        orderDetails?.company_code,
        location?.state?.stockist_po_attachment_id
      );
      const raw = res?.data?.data ?? res?.data ?? null;
      const orders = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const rows: any[] = [];
      orders.forEach((order: any) => {
        const orderCode = order?.orderCode ?? order?.order_code ?? "";
        const products = Array.isArray(order?.orderProducts) ? order.orderProducts : [];
        products.forEach((p: any) => {
          rows.push({
            order_product_id: p?.order_product_id ?? p?.id ?? null,
            orderCode,
            product_name: p?.product_name,
            item_code: p?.item_code,
            product_id: p?.product_id ?? p?.companyProducts?.id ?? null,
            quantity: p?.quantity,
            stockist_po_quantity: p?.stockist_po_quantity,
            stockist_po_freequantity: p?.stockist_po_freequantity,
            _isNew: false,
          });
        });
      });
      setPoViewData(rows);
      initialPoViewDataRef.current = JSON.parse(JSON.stringify(rows));
      if (!rows.length) {
        toast({ description: "No PO details found", variant: "default" });
      }
    } catch (error: any) {
      console.error("Error fetching PO details:", error);
      toast({
        description: error?.response?.data?.message || "Failed to fetch PO details",
        variant: "destructive",
      });
      setPoViewData([]);
      initialPoViewDataRef.current = [];
    } finally {
      setIsLoadingPoView(false);
    }
  };

  const handlePoViewFieldChange = (
    rIdx: number,
    field: "stockist_po_quantity" | "stockist_po_freequantity" | "product_name" | "item_code" | "product_id",
    value: any
  ) => {
    setPoViewData(prev => {
      if (!prev) return prev;
      return prev.map((r, i) => (i === rIdx ? { ...r, [field]: value } : r));
    });
  };

  const handlePoViewProductSelect = (rIdx: number, selected: any) => {
    const product_name = selected?.companyProducts?.name ?? "";
    const item_code = selected?.companyProducts?.item_code ?? "";
    const product_id = selected?.id ?? null;
    setPoViewData(prev => {
      if (!prev) return prev;
      return prev.map((r, i) => (i === rIdx ? { ...r, product_name, item_code, product_id } : r));
    });
  };

  const handleRemovePoViewRow = (rIdx: number) => {
    if (poUpdateTimers.current[rIdx]) {
      clearTimeout(poUpdateTimers.current[rIdx]);
      delete poUpdateTimers.current[rIdx];
    }
    setPoViewData(prev => (prev ? prev.filter((_, i) => i !== rIdx) : prev));
  };

  useEffect(() => {
    updateDetails()
  }, [orderDetails])

  // Auto-open "Edit PO Details" modal when the user arrives here via a PO-screen
  // deep-link (navigate state flag `autoOpenPoEdit`). Only runs once per mount
  // and respects the same module/company gate as the manual trigger.
  //M10-13811
  useEffect(() => {
    if (!autoOpenPoEdit) return;
    if (hasAutoOpenedPoEdit.current) return;
    if (!orderDetails?.order_id_int) return;
    if (!(orderDetails?.module_code == 4 && orderDetails?.company_code == '618')) return;
    setIsPoEditMode(true);
    hasAutoOpenedPoEdit.current = true;
  }, [autoOpenPoEdit, orderDetails?.order_id_int, orderDetails?.module_code, orderDetails?.company_code]);

  // After auto-opening the Edit PO modal via deep-link, also auto-trigger the
  // "View" action so the Product-wise PO Quantities table is loaded by default.
  // Gated on autoOpenPoView so it fires only for status-2 deep-links (not status-4).
  // Waits for stockist_po_number to be hydrated into poFormData.
  useEffect(() => {
    if (!autoOpenPoView) return;
    if (!isPoEditMode) return;
    if (hasAutoOpenedPoView.current) return;
    if (!(poFormData?.stockist_po_number || "").trim()) return;
    hasAutoOpenedPoView.current = true;
    handleViewPoDetails();
  }, [autoOpenPoView, isPoEditMode, poFormData?.stockist_po_number]);
  //M10-13811
  //M10-12866
  return (
    <Fragment>
      {checkingUINLoading && (
        <div className="fixed inset-x-0 top-0 z-50 backdrop-blur-md transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-center p-2 bg-gradient-to-r from-violet-600 via-purple-500 to-indigo-600 shadow-lg">
            <div className="flex items-center gap-4 px-4 py-1">
              <div className="relative w-6 h-6">
                <div className="absolute inset-0 rounded-full border-3 border-white/90 border-t-transparent animate-spin" />
                <div className="absolute inset-1 rounded-full bg-white/20 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-white tracking-wide">
                <span className="opacity-90">Checking UIN</span>
                <span className="inline-flex ml-1 animate-pulse">...</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {isFetchingOrderData ? (
        <div className="w-full pb-20 mt-10">
          <Loader />
        </div>
      ) : (
        <div className="w-full pb-20 mt-10">
          {orderDetailsFiltered?.length > 0 && (
            <div className="flex justify-between">
              <div>
                <p className="text-xl font-semibold">Order Draft</p>
                <p className="text-lg text-[#b5b5b5]">Review and manage authorized orders</p>
              </div>
              <div className="flex items-center gap-2">
                <>
                  {isCheckingPermission && (
                    <div className="flex items-center gap-2 mr-4">
                      <Spinner size="small" />
                      <span className="text-sm">Checking permissions...</span>
                    </div>
                  )}
                  {/* M10-14023 */}
                  {actionList?.includes("edit") && !isCheckingPermission && (
                    <Button
                      className="border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                      onClick={() => {
                        openEditProductsModal();
                      }}
                      loaderSize="small"
                      isLoading={isCheckingPermission}
                      module={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.module}
                      action={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.action}
                    >
                      {!isLoadingData && <img src={EditIcon} alt="counter_details_check" className="w-5 h-5 mr-2" />} Edit
                    </Button>
                  )}
                  {/* M10-14023 */}
                  {/* M10-16393: order:edit-quantity — shown on the RBAC right alone */}
                  {hasEditQuantityPermission && (
                    <Button
                      className="border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                      onClick={() => setIsQtyEditOpen(true)}
                      loaderSize="small"
                    >
                      <img src={EditIcon} alt="edit_quantity" className="w-5 h-5 mr-2" /> Edit Quantity
                    </Button>
                  )}

                  {actionList?.includes("approve") && !isCheckingPermission && (
                    <Button
                      className="border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                      onClick={async () => {
                        setActionType("approveCounter")
                        setModalStates({ ...modalStates, userConfirm: true });


                      }}
                      loaderSize="small"
                      isLoading={isCheckingPermission}
                      module={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.module}
                      action={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.action}
                    >
                      {!isLoadingData && <img src={SuccessIcon} alt="counter_details_check" className="w-full h-full mr-2" />} Approve
                    </Button>
                  )}
                  {actionList?.includes("reject") && !isCheckingPermission && (
                    <Button
                      className=" border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                      onClick={() => {
                        // setActionToPerformAfterUinVerification("reject");
                        // checkUINValidation();
                        setActionType("rejectCounter")
                        setModalStates({ ...modalStates, userConfirm: true });
                      }}
                      isLoading={isCheckingPermission}
                      module={fineGrainedPermissionsKeys.ORDER.ORDERREJECT.module}
                      action={fineGrainedPermissionsKeys.ORDER.ORDERREJECT.action}
                    >
                      <img src={CancelCircleIcon} alt="counter_details_cancel" className="w-full h-full mr-2" /> Reject
                    </Button>
                  )}
                </>
                <>
                  {" "}
                  <div className="flex items-center gap-2 mt-0 mr-4">
                    {/* M10-12866 */}
                    {/* {!isPoEditMode && orderDetails?.module_code == 4 && orderDetails?.company_code == '618' && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setIsPoEditMode(true);
                          }}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit PO Details
                        </button>
                      </div>
                    )} */}
                    {/* M10-12866 */}
                    <p className="text-[.9rem]  border border-[#E4E4E6] rounded-lg px-2 py-1 font-semibold">
                      Total Amount: <span className="text-[#6551ff]">{totalBilledPrice.toFixed(2)}</span>
                    </p>
                    <p className="text-[.9rem] font-semibold">
                      Total Qty. <span className="text-[#6551ff]">{totalQuantity}</span>
                    </p>
                  </div>
                </>
              </div>
            </div>
          )}
          {orderDetailsFiltered?.length === 0 && (
            <div className="flex justify-center items-center h-[20vh]">
              <p className="text-lg text-[#b5b5b5]">No order details found</p>
            </div>
          )}
          {orderDetailsFiltered?.length > 0 && (
            <div
              id="scrollableDiv"
              className="h-[calc(100vh-250px)] overflow-auto overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            >
              {orderDetails?.orderProducts?.filter((orders: any) => orders?.quantity > 0 || orders?.free_quantity > 0)?.map((orders: any, index: any) => (
                <OrderInfoInvoiceProducts
                  key={orders?.order_product_id}
                  cd={orderDetails?.order_cash_discount_perc}
                  order={orderDetails}
                  product={orders}
                  index={index}
                  item_code={orders?.item_code}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <FormSuccess
        showForm={modalStates.errorForm}
        onContinue={() => resetForm()}
        onCloseModal={() => { }}
        variant="error"
        popupMessage={{
          description: "You don't have permission to perform this action",
          title: "Access Denied!",
        }}
      />

      <FormSuccess
        onCloseModal={() => {
          closeModal("userConfirm");
          resetForm();
        }}
        showForm={modalStates.userConfirm}
        onContinue={() => {
          setModalStates(prev => ({
            ...prev,
            userConfirm: false,
            ...(actionType === "rejectCounter" && { [actionType]: true })
          }));
          if (actionType === "approveCounter") {
            (async () => {
              setIsLoadingData(true);

              const uinDetails = await getUinDetails(orderDetails?.counter_ff_div_lnk_id)
              setUinDetails(uinDetails?.data?.data)
              if (uinDetails?.data?.data) {
                if (uinDetails?.data?.data?.require_uin_status === true && uinDetails?.data?.data?.is_uin_required === true) {
                  const query = `?counter_company_location_id=${orderDetails?.counter_company_location_id}&stockist=true`;
                  const counterDetails = await getCounterByEmployee(query)
                  dispatch(ordActions.setSelectedCounterDataForUin(counterDetails?.data?.data))
                }
                setActionToPerformAfterUinVerification("approve");
              }
              setIsLoadingData(false);
              // checkUINValidation();
            })()
          }
        }}
        enableBackButton={true}
        variant="error"
        popupMessage={{
          description: `You are trying to ${actionType === "approveCounter" ? "approve" : "reject"
            } the order on behalf of ${extractLevel(orderDetails?.orderStatusHdr?.order_status_desc)}`,
          title: "Confirm Action",
        }}
      />

      <Modal
        isOpen={modalStates.approveCounter}
        isHeader={true}
        onCloseModal={() => {
          closeModal("approveCounter");
          resetForm();
          setActionToPerformAfterUinVerification("");
        }}
        title="Approve Order"
      >
        <OrderApprovalForm
          type={true}
          order_id={orderDetails?.order_id_int}
          position_code={currentPsr?.emp_position_hdr?.position_code}
          counter_company_location_id={orderDetails?.counter_company_location_id}
          closeModal={closeModal}
          onSuccess={(streamId: string) => {
            fetchOrderId(orderCode as string, streamId)
          }}
        />
      </Modal>
      {modalStates.rejectCounter && (
        <Modal
          isOpen={modalStates.rejectCounter}
          isHeader={true}
          title="Reject Order"
          onCloseModal={() => {
            closeModal("rejectCounter");
            resetForm();
          }}
        >
          <OrderApprovalForm
            onSuccess={(streamId: string) => {
              fetchOrderId(orderCode as string, streamId)
            }}
            order_id={orderDetails?.order_id_int}
            position_code={currentPsr?.emp_position_hdr?.position_code}
            type={false}
            closeModal={closeModal}
          />
        </Modal>
      )}
      {checkingUINOpen ? modal : null}
      {/* Modal Overlay - M10-12866*/}
      {isPoEditMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">Edit PO Details</h4>
              <div className="flex items-center gap-3">
                {poFilePathFromState && (
                  <Button
                    onClick={() => {
                      if (!poFilePathFromState) {
                        toast({ description: "PO not found", variant: "destructive" });
                        return;
                      }
                      downloadInvoice(poFilePathFromState, orderDetails?.company_code).then((res: any) => {
                        if (res?.data?.success && res?.data?.data) {
                          window.open(res?.data?.data, "_blank");
                        } else {
                          toast({ description: "Failed to download the PO.", variant: "destructive" });
                        }
                      }).catch(() => {
                        toast({ description: "Failed to download the PO.", variant: "destructive" });
                      });
                    }}
                    className="font-normal text-white bg-[#8051FF]"                  >
                    Download PO
                  </Button>
                )}
                <button
                  onClick={() => {
                    setIsPoEditMode(false);
                    updateDetails()
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg border border-indigo-100 mb-6">
                <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  General PO Information
                </h5>

                <div className="grid grid-cols-3 gap-4">
                  {/* PO Ref Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      PO Ref Number
                    </label>
                    <input
                      type="text"
                      value={poFormData?.poid_ref_number}
                      onChange={(e) => setPoFormData(prev => ({ ...prev, poid_ref_number: e.target.value }))}
                      placeholder="Enter PO Ref Number"
                      className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {/* PO Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      PO Date
                    </label>
                    <Input
                      enableArrows={false}
                      showIcon={false}
                      placeholder={"DD-MM-YYYY"}
                      type="date"
                      value={formatDateForInput(poFormData?.poid_date)}
                      className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      onChange={(e: any) => setPoFormData(prev => ({ ...prev, poid_date: e.target.value }))}
                    />
                  </div>

                  {/* PO Valid Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      PO Valid Date
                    </label>
                    <Input
                      enableArrows={false}
                      showIcon={false}
                      placeholder={"DD-MM-YYYY"}
                      type="date"
                      min={poFormData?.poid_date}
                      value={formatDateForInput(poFormData?.po_valid_date)}
                      onChange={(e) => setPoFormData(prev => ({ ...prev, po_valid_date: e.target.value }))}
                      className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {/* Stockist PO Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Stockist PO Number
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={poFormData?.stockist_po_number || ""}
                        onChange={(e) => setPoFormData(prev => ({ ...prev, stockist_po_number: e.target.value }))}
                        placeholder="Enter Stockist PO Number"
                        className="flex-1 h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleViewPoDetails}
                        disabled={isLoadingPoView || !(poFormData?.stockist_po_number || "").trim()}
                        className="h-10 px-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200 flex items-center gap-1.5"
                      >
                        {isLoadingPoView ? (
                          <Spinner size="small" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Table — visible only after View is clicked */}
              {poViewData !== null && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Product-wise PO Quantities
                    </h5>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                            Product Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            Product Code
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            Order Qty
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            PO Quantity
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                            PO Free Quantity
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orderDetails?.orderProducts.map((item: any, index: any) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            {/* Serial Number */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center w-8 h-8 text-sm font-semibold">
                                {index + 1}
                              </div>
                            </td>

                            {/* Product Name */}
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                {item?.product_name || "-"}
                              </div>
                            </td>

                            {/* Product Code */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="flex-1">
                                  {item?.item_code || "-"}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleOpenPriceList(item?.item_code)}
                                  disabled={!item.item_code}
                                  className="p-1.5 rounded-md hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                  title="View price list"
                                >
                                  <img src={EyeShowIcon} alt="View price list" className="w-5 h-5" />
                                </button>
                              </div>
                            </td>

                            {/* Order Quantity */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-3 py-1 text-sm">
                                {item?.quantity || "0"}
                              </span>
                            </td>

                            {/* PO Quantity Input */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Input
                                enableArrows={false}
                                showIcon={false}
                                type="number"
                                placeholder="Enter qty"
                                value={
                                  poFormData?.orderProducts?.find(
                                    q => q.order_product_id === item.order_product_id
                                  )?.stockist_po_quantity || ""
                                }
                                onChange={(e) =>
                                  setPoFormData((prev: any) => {
                                    const exists = prev.orderProducts.find(
                                      (q: any) => q.order_product_id === item.order_product_id
                                    );

                                    return {
                                      ...prev,
                                      orderProducts: exists
                                        ? prev.orderProducts.map((q: any) =>
                                          q.order_product_id === item.order_product_id
                                            ? { ...q, stockist_po_quantity: Number(e.target.value) }
                                            : q
                                        )
                                        : [
                                          ...prev.orderProducts,
                                          {
                                            order_product_id: item.order_product_id,
                                            stockist_po_quantity: Number(e.target.value),
                                          },
                                        ],
                                    };
                                  })
                                }
                                className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                isError={
                                  (
                                    poFormData?.orderProducts?.find(
                                      q => q.order_product_id === item.order_product_id
                                    )?.stockist_po_quantity || ""
                                  ).toString().length > 4
                                }
                                errorMessage="Maximum 4 digits allowed"
                                min="0"
                                max="9999"
                              />
                              {Number(item?.total_stockist_po_quantity) > 0 && (
                                <p className="mt-1 text-xs text-red-600">
                                  PO allocated quantity: {item.total_stockist_po_quantity}
                                </p>
                              )}
                            </td>

                            {/* ✅ PO FREE Quantity */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Input
                                enableArrows={false}
                                showIcon={false}
                                type="number"
                                placeholder="Free qty"
                                value={
                                  poFormData.orderProducts.find(
                                    q => q.order_product_id === item.order_product_id
                                  )?.stockist_po_freequantity ?? ""
                                }
                                onChange={(e) =>
                                  setPoFormData(prev => {
                                    const exists = prev.orderProducts.find(
                                      q => q.order_product_id === item.order_product_id
                                    );

                                    return {
                                      ...prev,
                                      orderProducts: exists
                                        ? prev.orderProducts.map(q =>
                                          q.order_product_id === item.order_product_id
                                            ? {
                                              ...q,
                                              stockist_po_freequantity: Number(e.target.value),
                                            }
                                            : q
                                        )
                                        : [
                                          ...prev.orderProducts,
                                          {
                                            order_product_id: item.order_product_id,
                                            stockist_po_quantity: 0, // default
                                            stockist_po_freequantity: Number(e.target.value),
                                          },
                                        ],
                                    };
                                  })
                                }
                                className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                isError={
                                  (
                                    poFormData.orderProducts.find(
                                      q => q.order_product_id === item.order_product_id
                                    )?.stockist_po_freequantity ?? ""
                                  ).toString().length > 4
                                } errorMessage="Maximum 4 digits allowed"
                                min="0"
                                max="9999"
                              />
                              {Number(item?.total_stockist_po_freequantity) > 0 && (
                                <p className="mt-1 text-xs text-red-600">
                                  PO allocated free quantity: {item.total_stockist_po_freequantity}
                                </p>
                              )}
                            </td>
                          </tr>
                        ))}

                        {/* Manually added product rows */}
                        {poFormData.newOrderProducts.map((row, index) => {
                          const baseIndex = (orderDetails?.orderProducts?.length || 0) + index + 1;
                          return (
                            <tr key={`new-${index}`} className="bg-indigo-50/40 hover:bg-indigo-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center w-8 h-8 text-sm font-semibold">
                                  {baseIndex}
                                </div>
                              </td>

                              {/* Product Name dropdown (portal-rendered to escape grid) */}
                              <td className="px-6 py-4">
                                <GridProductAutocomplete
                                  placeholder="Select product"
                                  suggestionList={productOptions}
                                  isLoading={isLoadingProducts}
                                  displayKey="companyProducts.name"
                                  selectedValue={{ product_name: row.product_name, item_code: row.item_code }}
                                  onSelectionChange={(value: any) => handleNewProductSelect(index, value)}
                                />
                                {isDuplicateNewProduct(index, row.item_code) && (
                                  <p className="text-xs text-red-600 mt-1">
                                    This product is already added
                                  </p>
                                )}
                              </td>

                              {/* Product Code dropdown (synced with Product Name via shared handler) */}
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <GridProductAutocomplete
                                      placeholder="Select code"
                                      suggestionList={productOptions}
                                      isLoading={isLoadingProducts}
                                      displayKey="companyProducts.item_code"
                                      selectedValue={{ product_name: row.product_name, item_code: row.item_code }}
                                      onSelectionChange={(value: any) => handleNewProductSelect(index, value)}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenPriceList(row.item_code)}
                                    disabled={!row.item_code}
                                    className="p-1.5 rounded-md hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    title="View price list"
                                  >
                                    <img src={EyeShowIcon} alt="View price list" className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>

                              {/* Order Qty (display only) */}
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700">
                                0
                              </td>

                              {/* PO Quantity (free text) */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Input
                                  enableArrows={false}
                                  showIcon={false}
                                  type="number"
                                  placeholder="PO qty"
                                  value={row.stockist_po_quantity}
                                  onChange={(e: any) =>
                                    handleNewProductFieldChange(index, "stockist_po_quantity", e.target.value)
                                  }
                                  className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  min="0"
                                  max="9999"
                                />
                              </td>

                              {/* PO Free Quantity (free text) + remove button */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <Input
                                    enableArrows={false}
                                    showIcon={false}
                                    type="number"
                                    placeholder="Free qty"
                                    value={row.stockist_po_freequantity}
                                    onChange={(e: any) =>
                                      handleNewProductFieldChange(index, "stockist_po_freequantity", e.target.value)
                                    }
                                    className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    min="0"
                                    max="9999"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveProductRow(index)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    title="Remove product"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Add Product button at the bottom */}
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddProductRow}
                      disabled={isLoadingProducts}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-medium rounded-md shadow-sm transition-colors duration-200 flex items-center gap-1.5"
                    >
                      {isLoadingProducts ? (
                        <Spinner size="small" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                      Add Product
                    </button>
                  </div>

                </div>
              )}

              {/* PO Details View — response grid */}
              {poViewData !== null && (
                <div className="border border-gray-200 rounded-lg overflow-hidden mt-6">
                  <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      PO Details for "{poFormData?.stockist_po_number}"
                    </h5>
                    <div className="flex items-center gap-3">

                      <button
                        type="button"
                        onClick={() => setPoViewData(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {poViewData.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500 text-center">No data found.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                              #
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              Order Code
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                              Product Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              Product Code
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              Order Qty
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              PO Quantity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                              PO Free Quantity
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {poViewData.map((row: any, rIdx: number) => (
                            <tr
                              key={rIdx}
                              className={`hover:bg-gray-50 transition-colors ${row?._isNew ? "bg-indigo-50/40" : ""}`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center w-8 h-8 text-sm font-semibold">
                                  {rIdx + 1}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {row?.orderCode || "-"}
                              </td>

                              {/* Product Name — Autocomplete for new rows, text for existing */}
                              <td className="px-6 py-4 text-sm">
                                {row?._isNew ? (
                                  <Autocomplete
                                    placeholder="Select product"
                                    suggestionList={productOptions}
                                    displayKey="companyProducts.name"
                                    additionalDisplayKey="companyProducts.item_code"
                                    additionalDisplayKeySearch={true}
                                    uniqueKey="id"
                                    defaultValue={
                                      row.product_id
                                        ? {
                                          id: row.product_id,
                                          companyProducts: { name: row.product_name, item_code: row.item_code },
                                        }
                                        : ""
                                    }
                                    onSelectionChange={(value: any) => handlePoViewProductSelect(rIdx, value)}
                                  />
                                ) : (
                                  row?.product_name || "-"
                                )}
                              </td>

                              {/* Product Code — Autocomplete for new rows, text for existing */}
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {row?._isNew ? (
                                  <Autocomplete
                                    placeholder="Select code"
                                    suggestionList={productOptions}
                                    displayKey="companyProducts.item_code"
                                    additionalDisplayKey="companyProducts.name"
                                    additionalDisplayKeySearch={true}
                                    uniqueKey="id"
                                    defaultValue={
                                      row.product_id
                                        ? {
                                          id: row.product_id,
                                          companyProducts: { name: row.product_name, item_code: row.item_code },
                                        }
                                        : ""
                                    }
                                    onSelectionChange={(value: any) => handlePoViewProductSelect(rIdx, value)}
                                  />
                                ) : (
                                  row?.item_code || "-"
                                )}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                                {row?.quantity ?? "0"}
                              </td>

                              {/* PO Quantity — editable */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Input
                                  enableArrows={false}
                                  showIcon={false}
                                  type="number"
                                  placeholder="PO qty"
                                  value={row?.stockist_po_quantity ?? ""}
                                  onChange={(e: any) =>
                                    handlePoViewFieldChange(rIdx, "stockist_po_quantity", e.target.value)
                                  }
                                  className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  min="0"
                                  max="9999"
                                />
                              </td>

                              {/* PO Free Quantity — editable + remove button on new rows */}
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <Input
                                    enableArrows={false}
                                    showIcon={false}
                                    type="number"
                                    placeholder="Free qty"
                                    value={row?.stockist_po_freequantity ?? ""}
                                    onChange={(e: any) =>
                                      handlePoViewFieldChange(rIdx, "stockist_po_freequantity", e.target.value)
                                    }
                                    className="w-full h-9 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    min="0"
                                    max="9999"
                                  />
                                  {row?._isNew && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemovePoViewRow(rIdx)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                      title="Remove row"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">

              <button
                disabled={isRequestInProgress.current}
                onClick={() => {
                  setIsPoEditMode(false);
                  updateDetails()
                }}
                className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
              >
                Cancel
              </button>
              <Button
                isLoading={isRequestInProgress.current}
                loaderSize="small"
                disabled={isRequestInProgress.current}
                type="submit"
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                // onClick={handleApproval}
                onClick={() => handleApproval()}
              >Submit</Button>
            </div>
          </div>
        </div>
      )}

      {/* M10-16393 */}
      {isQtyEditOpen && (
        <OrderQtyEditModal
          orderDetails={orderDetails}
          positionCode={currentPsr?.emp_position_hdr?.position_code}
          onClose={() => setIsQtyEditOpen(false)}
          onSaved={(streamId) => {
            setIsQtyEditOpen(false);
            fetchOrderId(orderCode as string, streamId);
          }}
        />
      )}

      {/* M10-14023 */}
      {editProductsModal.isOpen && (
        <Modal
          title="Edit Order Quantities"
          isHeader={true}
          isOpen={editProductsModal.isOpen}
          onCloseModal={closeEditProductsModal}
        >
          <SmartForm
            defaultValues={{ remarks: "" }}
            schema={editProductsSchema}
            onFormSubmit={handleSubmitEditProducts}
          >
            <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
              {editProductsModal.products.length === 0 ? (
                <p className="text-sm text-gray-500">No products available for this order.</p>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 pb-2 border-b">
                    <div className="col-span-5">Product Name</div>
                    <div className="col-span-2">Current Qty</div>
                    <div className="col-span-2">Current Free Qty</div>
                    <div className="col-span-3">New Qty</div>
                  </div>
                  {editProductsModal?.products?.map((p, idx) => (
                    <div
                      key={p.order_product_id}
                      className="grid grid-cols-12 gap-2 items-center py-2 border-b border-gray-100"
                    >
                      <div className="col-span-5 text-sm">{p.product_name} ({p.item_code})</div>
                      <div className="col-span-2 text-sm">{p.quantity}</div>
                      <div className="col-span-2 text-sm">{p.scheme_value}</div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          value={p.new_qty}
                          onChange={(e: any) => handleEditQtyChange(idx, e.target.value)}
                          className="w-full"
                        />
                        {getQtyError(p) && (
                          <p className="text-red-500 text-xs mt-1">{getQtyError(p)}</p>
                        )}
                        {p.price_identifier === 2 && (
                          <p className="text-xs text-gray-600 mt-1">
                            Free Qty: {getDerivedFreeQty(p)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <SmartForm.Input placeholder="" type="text" fieldName="remarks" className="w-1/3" label="Reference Number" />

              <footer className="flex gap-3 justify-end mt-2">
                <Button
                  onClick={closeEditProductsModal}
                  className="border"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmittingEditProducts}
                  loaderSize="small"
                  className="text-white bg-[#586AF5]"
                >
                  Submit & Approve
                </Button>
              </footer>
            </div>
          </SmartForm>
        </Modal>
      )}
      {/* M10-14023 */}
      {/* Invoice price list modal */}
      {priceListModalOpen && (
        <Modal
          title={`Price List${priceListItemCode ? ` - ${priceListItemCode}` : ""}`}
          isHeader={true}
          isOpen={priceListModalOpen}
          onCloseModal={() => setPriceListModalOpen(false)}
          onBack={() => setPriceListModalOpen(false)}
        >
          <div className="p-5 max-w-[90vw]">
            {isLoadingPriceList ? (
              <div className="flex justify-center py-10">
                <Spinner size="medium" />
              </div>
            ) : priceListData.length === 0 ? (
              <p className="text-sm font-semibold text-center text-gray-600 py-8">
                No data found
              </p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">Product Name</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">PTS</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">PTR</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">MRP</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">GST %</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">Remarks</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">Default</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {priceListData.map((p: any) => (
                      <tr key={p?.company_productprice_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">
                          {p?.company_productcode?.name || "-"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-800 text-right whitespace-nowrap">{p?.pts ?? "-"}</td>
                        <td className="px-4 py-2 text-sm text-gray-800 text-right whitespace-nowrap">{p?.ptr ?? "-"}</td>
                        <td className="px-4 py-2 text-sm text-gray-800 text-right whitespace-nowrap">{p?.mrp ?? "-"}</td>
                        <td className="px-4 py-2 text-sm text-gray-800 text-right whitespace-nowrap">{p?.gst_pc ?? "-"}</td>
                        <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">{p?.remarks || "-"}</td>
                        <td className="px-4 py-2 text-sm text-center text-green-600 font-bold whitespace-nowrap">
                          {p?.isDefault ? "✓" : ""}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">
                          {p?.expiration_date ? formatDateForInput(p.expiration_date) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <footer className="flex justify-end mt-5">
              <Button
                onClick={() => setPriceListModalOpen(false)}
                className="border bg-primary text-white px-6 py-[0.62rem]"
              >
                Close
              </Button>
            </footer>
          </div>
        </Modal>
      )}
    </Fragment>
  );
};

export default OrderInfoDetailsTab;
